import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { formatValue } from "../../utilities";
import { extractCountries } from "../../data/countryExplorerData";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styleConstants from "../../styleConstants";

const getSeries = (data, countries) => {
  const series = data.map(datum => ({
    x: countries[datum.country],
    indicator: datum.indicator,
    year: datum.year,
    y: datum.value
  }));

  return [
    {
      name: "2020",
      data: series.filter(datum => datum.year === 2020),
      color: "#cee0e9",
      marker: {
        fillColor: "#cee0e9"
      }
    },
    {
      name: "2025",
      data: series.filter(datum => datum.year === 2025),
      color: "#8db8cb",
      marker: {
        fillColor: "#8db8cb"
      }
    },
    {
      name: "2030",
      data: series.filter(datum => datum.year === 2030),
      color: styleConstants.colors.themeBlue,
      marker: {
        fillColor: styleConstants.colors.themeBlue
      }
    }
  ];
};

const drawVelocityIndicator = (chart, countryCount, countryIndex, y1, y2) => {
  const countryOffset = countryCount - countryIndex;

  const xAxis = chart.xAxis[0];
  const yAxis = chart.yAxis[0];

  const barGap = xAxis.translate(countryCount - 1.5);
  const barCenter = xAxis.top + xAxis.translate(countryOffset) + barGap;
  const barHeight = 12;

  const increase = y2 - y1;

  const gradient = {
    linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
    stops:
      increase > 0
        ? [[0, "white"], [1, styleConstants.colors.themeBlueMedium]]
        : [[0, styleConstants.colors.themeBlueMedium], [1, "white"]]
  };

  chart.renderer
    .rect(
      yAxis.left + yAxis.translate(Math.min(y1, y2)),
      barCenter - barHeight / 2,
      yAxis.translate(Math.abs(increase)),
      barHeight
    )
    .attr({
      fill: gradient,
      zIndex: -1
    })
    .add();
};

const drawVelocityIndicators = (
  chart,
  countries,
  earlierSeries,
  laterSeries
) => {
  const countryCount = Object.keys(countries).length;

  const earlierSeriesData = earlierSeries.data.sort((a, b) => a.x - b.x);
  const laterSeriesData = laterSeries.data.sort((a, b) => a.x - b.x);

  for (let i = 0; i < countryCount; i++) {
    drawVelocityIndicator(
      chart,
      countryCount,
      i,
      earlierSeriesData[i].y,
      laterSeriesData[i].y
    );
  }
};

const drawAllVelocityIndicators = (chart, countries, series) => {
  drawVelocityIndicators(chart, countries, series[0], series[1]);
  drawVelocityIndicators(chart, countries, series[1], series[2]);
};

const formatTooltip = (point, indicator, method) => {
  let text = method ? `${method} ${indicator}` : indicator;

  const year = parseInt(point.series.name, 10);

  text = `${text}, ${year}, ${formatValue(point.y)}`;

  if (year > 2020) {
    const previousSeries = point.series.chart.series[point.series.index - 1];
    const previousSeriesPoint = previousSeries.data[point.point.index];

    const change = point.y - previousSeriesPoint.y;

    text = `${text},<br />Change (${year - 5}-${year}): ${formatValue(
      change
    )} (${formatValue(change / previousSeriesPoint.y, true)})`;
  }

  return text;
};

const CountryExplorerProjectionChart = props => {
  const countries = extractCountries(props.data);

  const series = getSeries(props.data, countries);

  const options = {
    chart: {
      type: "scatter",
      height: props.height,
      inverted: true,
      backgroundColor: null,
      style: {
        fontFamily: "'Source Sans Pro', sans-serif;"
      },
      events: {
        redraw: function() {
          drawAllVelocityIndicators(this, countries, series);
        }
      }
    },
    plotOptions: {
      series: {
        marker: {
          symbol: "circle",
          radius: 6,
          lineWidth: 1,
          lineColor: styleConstants.colors.gray6
        },
        states: {
          inactive: {
            opacity: 1
          }
        }
      }
    },
    title: {
      text: undefined
    },
    xAxis: {
      categories: Object.keys(countries),
      lineWidth: 0,
      tickmarkPlacement: "on",
      gridLineWidth: 1,
      gridLineDashStyle: "ShortDot",
      gridLineColor: styleConstants.colors.gray4,
      labels: {
        style: {
          fontSize: "1rem",
          color: styleConstants.colors.gray6
        }
      }
    },
    yAxis: {
      opposite: true,
      offset: 20,
      lineWidth: 1,
      lineColor: styleConstants.colors.gray4,
      tickColor: styleConstants.colors.gray4,
      tickPixelInterval: 100,
      tickWidth: 1,
      tickPosition: "inside",
      tickLength: 5,
      gridLineWidth: 0,
      title: {
        enabled: false
      },
      labels: {
        autoRotation: false,
        style: {
          whiteSpace: "nowrap",
          fontSize: "1rem",
          color: "lightgray"
        }
      }
    },
    series,
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      formatter: function() {
        return formatTooltip(this, props.indicator, props.method);
      },
      style: {
        fontSize: "0.875rem",
        color: styleConstants.colors.gray6
      }
    },
    legend: {
      verticalAlign: "top"
    },
    exporting: {
      enabled: false
    },
    credits: {
      enabled: false
    }
  };

  return (
    <HighchartsReact
      ref={props.innerRef}
      highcharts={Highcharts}
      options={options}
      immutable={true}
      callback={chart => {
        chart.redraw();
      }}
    />
  );
};

CountryExplorerProjectionChart.propTypes = {
  data: PropTypes.array.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  indicator: PropTypes.string.isRequired,
  method: PropTypes.string
};

export default memo(
  React.forwardRef((props, ref) => (
    <CountryExplorerProjectionChart innerRef={ref} {...props} />
  ))
);
